import React, { useMemo, useState } from 'react';
import { FaChevronRight, FaGripVertical } from 'react-icons/fa';
import { useInsert } from './InsertContext';

// Datos de un paso, como en n8n: en TABLA (cuando son filas) o en JSON. Cada campo se puede ARRASTRAR a un campo de la configuración
// o se le hace CLIC para meterlo en el último campo que usaste (si no hay ninguno, se copia). `base`: la ruta de esos datos, por ejemplo
// «nodos.buscar» o «entrada».
const SAFE = /^[A-Za-z0-9_-]+$/;
const pathOf = (base, k) => (SAFE.test(String(k)) ? `${base}.${k}` : null);
const isRows = (v) => Array.isArray(v) && v.length > 0 && v.every((x) => x && typeof x === 'object' && !Array.isArray(x));

export default function DataView({ value, base, pickable = true, emptyText = 'Sin datos.' }) {
  // Si es la salida de un conector ({status, data}), las filas están en data.
  const rowsAt = isRows(value) ? { rows: value, base } : value && typeof value === 'object' && isRows(value.data) ? { rows: value.data, base: `${base}.data` } : null;
  const [mode, setMode] = useState(rowsAt ? 'table' : 'json');
  const { insert } = useInsert();
  const [flash, setFlash] = useState('');
  const pick = (path) => {
    if (!pickable || !path) return;
    const t = `{{${path}}}`;
    const done = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 1500); };
    if (insert(t)) done(`Insertado ${t}`);
    else navigator.clipboard?.writeText(t).then(() => done(`Copiado ${t}`)).catch(() => done(t));
  };
  if (value === undefined || value === null || (typeof value === 'object' && !Object.keys(value).length)) return <p className="text-[12.5px] text-fg-faint">{emptyText}</p>;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-2">
        {rowsAt && (
          <div role="tablist" aria-label="Ver como" className="inline-flex gap-0.5 rounded-full bg-raised p-0.5 ring-1 ring-inset ring-line">
            {[{ k: 'table', t: 'Tabla' }, { k: 'json', t: 'JSON' }].map((o) => (
              <button key={o.k} type="button" role="tab" aria-selected={mode === o.k} onClick={() => setMode(o.k)}
                className={`h-6 px-2.5 rounded-full text-[11.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${mode === o.k ? 'bg-panel text-fg ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>{o.t}</button>
            ))}
          </div>
        )}
        {rowsAt && <span className="text-[11.5px] text-fg-faint tabular-nums">{rowsAt.rows.length} {rowsAt.rows.length === 1 ? 'fila' : 'filas'}</span>}
        <span role="status" aria-live="polite" className="ml-auto truncate text-[11.5px] text-live font-mono">{flash}</span>
      </div>
      {mode === 'table' && rowsAt ? <Table rows={rowsAt.rows} base={rowsAt.base} onPick={pickable ? pick : null} />
        : <div className="font-mono text-[12px] leading-relaxed"><Node value={value} path={base} name={null} depth={0} onPick={pickable ? pick : null} /></div>}
    </div>
  );
}

function Chip({ path, children, onPick, className = '' }) {
  if (!onPick || !path) return <span className={className}>{children}</span>;
  return (
    <button type="button" draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', `{{${path}}}`); e.dataTransfer.effectAllowed = 'copy'; }}
      onClick={() => onPick(path)} title={`Arrastra o haz clic para usar {{${path}}}`}
      className={`group inline-flex items-center gap-1 rounded px-1 -mx-1 cursor-grab hover:bg-live-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${className}`}>
      <FaGripVertical aria-hidden className="h-2 w-2 opacity-0 group-hover:opacity-50" />{children}
    </button>
  );
}

function Table({ rows, base, onPick }) {
  const cols = useMemo(() => [...new Set(rows.slice(0, 30).flatMap((r) => Object.keys(r)))].slice(0, 30), [rows]);
  const cell = (v) => (v === null || v === undefined ? <span className="text-fg-faint">null</span> : typeof v === 'object' ? <span className="text-fg-dim">{JSON.stringify(v).slice(0, 60)}</span> : String(v).slice(0, 120));
  return (
    <div className="overflow-auto rounded-lg ring-1 ring-line">
      <table className="w-full text-[12px]">
        <thead className="bg-raised/60 sticky top-0">
          <tr>{cols.map((c) => (
            <th key={c} scope="col" className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
              <Chip path={pathOf(`${base}.0`, c)} onPick={onPick} className="font-mono text-[rgb(var(--live))]">{c}</Chip>
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.slice(0, 30).map((r, i) => (
            <tr key={i} className="border-t border-line">
              {cols.map((c) => <td key={c} className="px-2 py-1 align-top whitespace-nowrap max-w-[220px] truncate"><Chip path={pathOf(`${base}.${i}`, c)} onPick={onPick}>{cell(r[c])}</Chip></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Node({ value, path, name, depth, onPick }) {
  const [open, setOpen] = useState(depth < 2);
  const isObj = value !== null && typeof value === 'object';
  const key = name === null ? null : <Chip path={path} onPick={onPick} className="text-[rgb(var(--live))]">{name}</Chip>;
  if (!isObj) {
    const shown = typeof value === 'string' ? `"${value}"` : String(value);
    return <div className="pl-4">{key}{key && <span className="text-fg-faint">: </span>}<span className={typeof value === 'string' ? 'text-fg break-words' : 'text-caution'}>{shown}</span></div>;
  }
  const entries = Array.isArray(value) ? value.map((v, i) => [String(i), v]) : Object.entries(value);
  return (
    <div className={depth ? 'pl-4' : ''}>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={open ? 'Contraer' : 'Expandir'}
          className="h-4 w-4 grid place-items-center text-fg-faint hover:text-fg rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
          <FaChevronRight aria-hidden className={`h-2 w-2 transition-transform ${open ? 'rotate-90' : ''}`} />
        </button>
        {key}
        <span className="text-fg-faint">{Array.isArray(value) ? `[${value.length}]` : `{${entries.length}}`}</span>
      </div>
      {open && entries.map(([k, v]) => {
        const p = pathOf(path, k);
        return p ? <Node key={k} value={v} path={p} name={k} depth={depth + 1} onPick={onPick} /> : <div key={k} className="pl-4 text-fg-faint">{k}: {JSON.stringify(v)}</div>;
      })}
    </div>
  );
}

/** «3 filas», «1 objeto», «texto»: resumen corto de una salida para el lienzo. */
export function summarize(out) {
  if (out === undefined) return null;
  const v = out && typeof out === 'object' && 'data' in out ? out.data : out;
  if (Array.isArray(v)) return `${v.length} ${v.length === 1 ? 'fila' : 'filas'}`;
  if (v && typeof v === 'object') return `${Object.keys(v).length} campos`;
  if (typeof v === 'string') return 'texto';
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  return v === null ? 'vacío' : String(v);
}
